/**
 * BottomSheet — Adaptive overlay component
 *
 * Mobile (< 768px): Slide-up bottom sheet with drag handle
 * Desktop (≥ 768px): Centered modal with backdrop blur
 *
 * Uses createPortal to body to avoid z-index/overflow issues.
 *
 * Props:
 *   open      — boolean, controls visibility
 *   onClose   — callback to dismiss
 *   title     — optional header text
 *   children  — content
 *   maxHeight — optional, default "90vh"
 *   zIndex    — optional overlay priority, default 99999
 */
"use client";

import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";

// ============================================================================
// STYLES
// ============================================================================

const backdropBase = {
    position: "fixed",
    inset: 0,
    zIndex: 99999,
    display: "flex",
    animation: "bsFadeIn 0.2s ease-out",
};

const mobileBackdrop = {
    ...backdropBase,
    background: "rgba(0, 0, 0, 0.55)",
    alignItems: "flex-end",
};

const desktopBackdrop = {
    ...backdropBase,
    background: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    alignItems: "center",
    justifyContent: "center",
};

const mobileSheet = (maxHeight) => ({
    width: "100%",
    maxHeight,
    minHeight: "30vh",
    background:
        "linear-gradient(145deg, rgba(18,18,18,0.99) 0%, rgba(12,12,12,0.99) 100%)",
    borderRadius: "20px 20px 0 0",
    boxShadow: "0 -8px 40px rgba(0, 0, 0, 0.4)",
    animation: "bsSlideUp 0.3s cubic-bezier(0.32, 0.72, 0, 1)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    direction: "rtl",
});

const desktopModal = {
    width: "90%",
    maxWidth: 560,
    maxHeight: "85vh",
    background:
        "linear-gradient(135deg, rgba(26,26,26,0.98) 0%, rgba(16,16,16,0.98) 100%)",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
    animation: "bsScaleIn 0.2s ease-out",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    direction: "rtl",
};

const handleStyle = {
    width: 40,
    height: 4,
    background: "rgba(255,255,255,0.25)",
    borderRadius: 2,
    margin: "10px auto 6px",
    flexShrink: 0,
};

const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 20px 8px",
    flexShrink: 0,
};

const titleStyle = {
    fontSize: 18,
    fontWeight: 700,
    fontFamily: "'AnjomanMax', sans-serif",
    color: "#fff",
    flex: 1,
};

const closeBtnStyle = {
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    color: "rgba(255,255,255,0.6)",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    transition: "background 0.15s",
};

const contentStyle = {
    flex: 1,
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "8px 20px 24px",
};

// ============================================================================
// GLOBAL CSS (injected once)
// ============================================================================

const KEYFRAMES_ID = "bottom-sheet-keyframes";

function injectKeyframes() {
    if (typeof document === "undefined") return;
    if (document.getElementById(KEYFRAMES_ID)) return;

    const style = document.createElement("style");
    style.id = KEYFRAMES_ID;
    style.textContent = `
    @keyframes bsFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes bsSlideUp {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }
    @keyframes bsScaleIn {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
  `;
    document.head.appendChild(style);
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function BottomSheet({
    open,
    onClose,
    title,
    children,
    maxHeight = "90vh",
    zIndex = 99999,
}) {
    const [isMobile, setIsMobile] = useState(false);
    const [mounted, setMounted] = useState(false);

    // Track viewport
    useEffect(() => {
        setMounted(true);
        injectKeyframes();

        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    // ESC to close
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [open, onClose]);

    // Lock body scroll when open
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    const handleBackdropClick = useCallback(
        (e) => {
            if (e.target === e.currentTarget) onClose();
        },
        [onClose]
    );

    if (!mounted || !open) return null;

    const sheet = (
        <div
            style={{
                ...(isMobile ? mobileBackdrop : desktopBackdrop),
                zIndex,
            }}
            onClick={handleBackdropClick}
        >
            <div
                style={isMobile ? mobileSheet(maxHeight) : desktopModal}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Drag handle (mobile only) */}
                {isMobile && <div style={handleStyle} />}

                {/* Header */}
                {title && (
                    <div style={headerStyle}>
                        <div style={titleStyle}>{title}</div>
                        <button
                            type="button"
                            style={closeBtnStyle}
                            onClick={onClose}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.background = "rgba(255,255,255,0.12)")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.background = "rgba(255,255,255,0.06)")
                            }
                            aria-label="بستن"
                        >
                            ✕
                        </button>
                    </div>
                )}

                {/* Content */}
                <div style={contentStyle}>{children}</div>
            </div>
        </div>
    );

    return createPortal(sheet, document.body);
}
